/**
 * The reader-lens contract.
 *
 * Deliberately its own module with **no runtime imports**: these constants and
 * the rules around them are shared with two other repos — `lens::plan::parse` in
 * feeder-rs is the serve-path authority and `normalise_lens` in feed-processor
 * guards the mapping payload — and keeping the logic free of app dependencies is
 * what lets it be read, reasoned about, and exercised on its own rather than
 * only through a mounted canvas.
 *
 * Changing a string here changes behaviour in those repos. A value none of the
 * three recognises silently means "no lens".
 */

/**
 * Which slice of the reader's own graph a lens keeps.
 *
 * Values are the wire contract with feeder-rs and with feed-processor's
 * `SUPPORTED_LENS_FACETS` — changing a string here changes behaviour in two
 * other repos, and a value none of them recognise silently means "no lens".
 *
 * `follows` alone is usually sparse: measured across live feeds, most readers
 * share no first-degree author with a given feed at all, so a strict follows
 * lens changes almost nothing. `network` exists for that reason — it ranks
 * first-degree authors above second-degree ones and falls back through them, so
 * a thin graph degrades into a reordering rather than a no-op.
 */
export const lensFacets = [
  "follows",
  "follows2",
  "network",
  "niche",
  "popular",
  "velocity",
  "community",
  "discover",
  "expertise",
] as const;
type LensFacet = (typeof lensFacets)[number];

/**
 * ⚠️ A lens RANKS. It only removes posts when the reader's graph covers enough
 * of the page to still fill it — see `lens/blend.rs` in feeder-rs: 94% of
 * (feed, reader) pairs have zero first-degree overlap, so a strict
 * `author ∈ follows` filter matched nothing on almost every feed, and the
 * fallback is to serve the whole page with graph-backed items promoted.
 *
 * So these labels must not promise omission. "People I follow" reading as a
 * filter is exactly the report this copy came from: on a feed of news accounts
 * the reader doesn't follow, choosing it changes nothing visible, and the old
 * hint ("Strictest. On many feeds this matches very little.") described a
 * filter that does not exist.
 */
export const lensFacetOptions: {
  value: LensFacet;
  label: string;
  hint: string;
}[] = [
  {
    value: "follows",
    label: "Prioritize people I follow",
    hint: "Puts authors you follow first. It only hides the rest when you follow enough of this feed's authors to still fill the page — on most feeds that means reordering, not filtering.",
  },
  {
    value: "follows2",
    label: "Prioritize people my follows follow",
    hint: "Much wider. Ranks each author by how many of your follows reach them.",
  },
  {
    value: "network",
    label: "Prioritize my network",
    hint: "Recommended. People you follow first, then who they follow, then everything else.",
  },
  {
    value: "niche",
    label: "Prioritize my niche favorites",
    hint: "Accounts your network loves that the wider world hasn't found. Discovery-leaning.",
  },
  {
    value: "popular",
    label: "Prioritize what's popular near me",
    hint: "Your wider network, tilted toward accounts everyone knows.",
  },
  {
    value: "velocity",
    label: "What my network is discovering",
    hint: "Accounts people around you started following in the last week.",
  },
  {
    value: "community",
    label: "Prioritize my communities",
    hint: "The social clusters your follows belong to, beyond who you follow directly.",
  },
  {
    value: "discover",
    label: "Discovery mix",
    hint: "Niche favorites and fresh finds first. Deliberately excludes people you already follow.",
  },
  {
    value: "expertise",
    label: "Prioritize experts in this feed's world",
    hint: "People this feed's own authors follow most, boosted by your own network. Recency-weighted.",
  },
];

/**
 * The lens contract's structured form — a blend a creator composes themselves
 * when no preset says what they mean.
 *
 * Three repos parse this and all three must agree: `lens::plan::parse` in
 * feeder-rs is the serve-path authority, `normalise_lens` in feed-processor
 * guards the mapping payload, and this file is what writes it. The rules below
 * are theirs, mirrored here so the editor cannot save something the serve path
 * would silently drop.
 *
 * Frozen by `v`. A preset name stays the recommended form precisely because it
 * is NOT frozen — what `network` weighs is retunable server-side, while a blend
 * written here means exactly what it says forever.
 */
const LENS_CONTRACT_VERSION = 1;

/**
 * Facets a blend may name: the ones with a stored blob of their own.
 *
 * Deliberately not `lensFacets`. The composites (`network`, `discover`,
 * `expertise`) are recipes *over* these, so naming one inside a blend would pull
 * three blobs and blow the entry cap — they are presets only. `domain` is the
 * mirror case: it has a blob and is blendable, but it is feed-scoped rather than
 * a reader's own graph, so it is not offered as a standalone preset.
 */
export const blendableLensFacets = [
  "follows",
  "follows2",
  "niche",
  "popular",
  "velocity",
  "community",
  "domain",
] as const;
export type BlendableLensFacet = (typeof blendableLensFacets)[number];

/**
 * Entries one blend may hold. Each costs a blob fetch on the serve path, so this
 * is a latency budget rather than a matter of taste.
 */
export const MAX_LENS_BLEND_ENTRIES = 6;

export const lensCombineModes = ["max", "sum"] as const;
type LensCombine = (typeof lensCombineModes)[number];

export const lensPolicies = ["boost", "filter"] as const;
type LensPolicy = (typeof lensPolicies)[number];

interface LensBlendEntry {
  facet: BlendableLensFacet;
  weight: number;
}

interface LensBlend {
  v: typeof LENS_CONTRACT_VERSION;
  blend: LensBlendEntry[];
  combine: LensCombine;
  policy: LensPolicy;
}

/** Every form the `lens` setting accepts. */
export type LensSetting = LensFacet | LensBlend | AudiencePlan | AudiencePlanV3;

export const blendableLensFacetOptions: {
  value: BlendableLensFacet;
  label: string;
  hint: string;
}[] = [
  {
    value: "follows",
    label: "People they follow",
    hint: "First degree. Strongest signal, but sparse on most feeds.",
  },
  {
    value: "follows2",
    label: "People their follows follow",
    hint: "Second degree, weighted by how many of their follows reach each author.",
  },
  {
    value: "niche",
    label: "Their niche favorites",
    hint: "Second degree discounted by global fame — nearby accounts the wider world hasn't found.",
  },
  {
    value: "popular",
    label: "Popular near them",
    hint: "Second degree boosted by global fame — nearby and widely known.",
  },
  {
    value: "velocity",
    label: "What their network is discovering",
    hint: "Reached through follows made in the last week.",
  },
  {
    value: "community",
    label: "Their communities",
    hint: "Members of the clusters their follows concentrate in.",
  },
  {
    value: "domain",
    label: "This feed's own authorities",
    hint: "Who this feed's recent authors collectively follow. The same for every reader — the one facet that isn't personal.",
  },
];

export const lensCombineOptions: {
  value: LensCombine;
  label: string;
  hint: string;
}[] = [
  {
    value: "max",
    label: "Strongest signal wins",
    hint: "Right when the facets are views of the same relationship — being reachable twice is that relationship counted twice.",
  },
  {
    value: "sum",
    label: "Signals add up",
    hint: "Right when the facets are different facts about an account, where holding both really is stronger than either.",
  },
];

export const lensPolicyOptions: {
  value: LensPolicy;
  label: string;
  hint: string;
}[] = [
  {
    value: "boost",
    label: "Reorder only",
    hint: "Every post still reaches the reader; matches move to the top. Safe default.",
  },
  {
    value: "filter",
    label: "Reorder and trim",
    hint: "Drops the rest once there are enough matches to fill a page. Below that it falls back to reordering.",
  },
];

/** Whether a `lens` setting is a structured blend THIS build can read. */
export function isLensBlend(lens: unknown): lens is LensBlend {
  if (typeof lens !== "object" || lens === null) return false;
  const candidate = lens as Partial<LensBlend>;
  // The version check is the whole point of `v`. A blend written for a later
  // contract is not a blend we can edit, and pretending otherwise would put
  // facets we do not understand into rows the creator could not fix.
  return (
    candidate.v === LENS_CONTRACT_VERSION && Array.isArray(candidate.blend)
  );
}

/**
 * The lens setting if this build can interpret it, otherwise `undefined`.
 *
 * The editor's counterpart to the serve path's total function: `lens` lives in
 * free-form JSONB, so it may hold a preset renamed since, a blend from a later
 * contract, or something that was never a lens. All of those mean "no lens" to
 * the reader, and they must mean the same here — an editor that crashes on a
 * setting the serve path shrugs at is worse than one that shows it as off.
 */
export function readableLens(lens: unknown): LensSetting | undefined {
  if (typeof lens === "string") {
    return (lensFacets as readonly string[]).includes(lens)
      ? (lens as LensFacet)
      : undefined;
  }
  if (isAudiencePlan(lens)) return lens;
  if (isAudiencePlanV3(lens)) return lens;
  return isLensBlend(lens) ? lens : undefined;
}

/**
 * A blend the serve path will actually honour, or `null` for "no lens".
 *
 * Mirrors the two backend parsers rather than trusting them, so the editor never
 * saves something that silently serves unlensed. Weights clamp into range and
 * duplicates collapse to their strongest mention — a facet named twice would be
 * counted twice under "sum", quietly doubling what it vouches for.
 */
export function normaliseLensBlend(blend: LensBlend): LensBlend | null {
  const seen = new Map<BlendableLensFacet, number>();
  for (const entry of blend.blend) {
    if (!blendableLensFacets.includes(entry.facet)) continue;
    if (!Number.isFinite(entry.weight) || entry.weight < 0) continue;
    const weight = Math.min(1, Math.max(0, entry.weight));
    const existing = seen.get(entry.facet);
    seen.set(entry.facet, existing === undefined ? weight : Math.max(existing, weight));
  }
  if (seen.size === 0 || seen.size > MAX_LENS_BLEND_ENTRIES) return null;
  return {
    v: LENS_CONTRACT_VERSION,
    blend: [...seen].map(([facet, weight]) => ({ facet, weight })),
    combine: blend.combine,
    policy: blend.policy,
  };
}

/** Reader-facing one-liner for whichever form the setting takes. */
export function describeLensSetting(lens: LensSetting): string {
  if (isAudiencePlan(lens)) return describeAudiencePlan(lens);
  if (isAudiencePlanV3(lens)) return describeAudiencePlanV3(lens);
  if (!isLensBlend(lens)) {
    return lensFacetOptions.find((o) => o.value === lens)?.hint ?? "";
  }
  const names = lens.blend
    .map(
      (e) =>
        blendableLensFacetOptions.find((o) => o.value === e.facet)?.label ??
        e.facet,
    )
    .join(", ");
  const combine = lens.combine === "sum" ? "added together" : "strongest wins";
  const policy =
    lens.policy === "filter" ? "trimmed to matches" : "reordered only";
  return `Custom blend of ${names} — ${combine}, ${policy}.`;
}

/* ==========================================================================
 * The audience contract (v2): filters that remove, boosts that reorder.
 *
 * v1 had one dial, `policy`, and it was not honest. `filter` removed posts only
 * when the reader's graph covered enough of the page to still fill it, and
 * reordered otherwise — measured, 94% of (feed, reader) pairs. A creator who
 * picked "People I follow" was told the feed would be limited to people they
 * follow and got a reordering, with nothing on screen to say so.
 *
 * v2 makes the two behaviours things a creator asks for by name, as two
 * structurally distinct sections rather than one list of tagged layers: a
 * filter cannot silently become a reorder because there is no field it could
 * set to do so. The bug is prevented by the shape, not by a rule someone
 * relaxes later.
 *
 * As with v1, three repos parse this and all three must agree —
 * `lens::layers::parse` in feeder-rs is the serve-path authority,
 * `_normalise_lens_v2` in feed-processor guards the mapping payload, and this
 * file is what writes it.
 * ========================================================================== */

export const AUDIENCE_CONTRACT_VERSION = 2;

/**
 * Facets whose stored count means "how many of your follows".
 *
 * `follows` is the boolean one — you follow someone or you do not — so a
 * threshold on it is a category error, not a tighter filter, and all three
 * parsers refuse it rather than ignoring it. Every other viewer facet stores
 * the same raw co-follower reach, which is why "at least 5" means the same
 * thing whichever fame prior the creator picked.
 *
 * `domain` counts a different population (this feed's own authors), which is
 * why a threshold belongs to its layer and units never mix in one comparison.
 */
export const countableLensFacets = blendableLensFacets.filter(
  (f) => f !== "follows",
);

export function isCountableFacet(facet: BlendableLensFacet): boolean {
  return facet !== "follows";
}

/** What a filter does when it would leave the reader with nothing. */
export const audienceOnEmptyModes = ["strict", "unfiltered"] as const;
export type AudienceOnEmpty = (typeof audienceOnEmptyModes)[number];

interface AudienceFilter {
  facet: BlendableLensFacet;
  /** "at least N of the people they follow". Countable facets only. */
  min?: number;
  on_empty: AudienceOnEmpty;
}

interface AudienceBoost {
  /**
   * How far the boost may move the page, 0–1.
   *
   * Per-plan rather than per-layer: it answers "how much may this rearrange the
   * feed", which is one question about the feed, where a layer's `weight`
   * answers "how much is this signal worth", which is one per layer.
   */
  strength: number;
  combine: LensCombine;
  blend: LensBlendEntry[];
}

export interface AudiencePlan {
  v: typeof AUDIENCE_CONTRACT_VERSION;
  filters: AudienceFilter[];
  boost: AudienceBoost | null;
}

/** Largest `min` a filter may ask for; mirrors the backends. */
export const MAX_AUDIENCE_MIN_COUNT = 65534;

/* ==========================================================================
 * The audience contract, v3: filters compose with any/all.
 *
 * v2's filters are conjunctive, and for graph membership that is the wrong
 * default more often than not — `follows2` excludes people you already
 * follow, so "follows AND follows2" is empty by construction, which is what
 * the one live feed with filters had asked for. v3 makes `filter` a TREE: a
 * facet leaf (`{facet, min?}`) or an `and`/`or` group of trees, and moves
 * `on_empty` to the plan, where v2 effectively had it (the strictest filter
 * decided for the page).
 *
 * The editor keeps WRITING v2 whenever the tree is a flat conjunction — a leaf,
 * or an `and` of leaves — and writes v3 only when the creator has drawn an
 * `or` or nested a group. That is what makes the rollout safe: every existing
 * feed stays on the proven path, and only a plan that could not be expressed
 * before exercises the new one. `isFlatConjunction` is that rule.
 *
 * Three repos parse this and must agree: `lens::layers::parse_v3` in
 * feeder-rs, `_normalise_lens_v3` in feed-processor, and this file.
 * ========================================================================== */

export const AUDIENCE_CONTRACT_VERSION_V3 = 3;
/** Mirrors `MAX_FILTER_LEAVES` / `MAX_FILTER_DEPTH` in feeder-rs. */
export const MAX_AUDIENCE_FILTER_LEAVES = 16;
export const MAX_AUDIENCE_FILTER_DEPTH = 3;

interface AudienceFilterLeaf {
  facet: BlendableLensFacet;
  min?: number;
}
export type AudienceFilterNode =
  | AudienceFilterLeaf
  | { and: AudienceFilterNode[] }
  | { or: AudienceFilterNode[] };

export interface AudiencePlanV3 {
  v: typeof AUDIENCE_CONTRACT_VERSION_V3;
  filter: AudienceFilterNode | null;
  on_empty: AudienceOnEmpty;
  boost: AudienceBoost | null;
}

export function isAudiencePlanV3(lens: unknown): lens is AudiencePlanV3 {
  if (typeof lens !== "object" || lens === null) return false;
  const candidate = lens as Partial<AudiencePlanV3>;
  return candidate.v === AUDIENCE_CONTRACT_VERSION_V3 && "filter" in candidate;
}

export function isFilterLeaf(node: AudienceFilterNode): node is AudienceFilterLeaf {
  return "facet" in node;
}

/** Every facet leaf in the tree, document order, repeats included. */
export function filterTreeLeaves(node: AudienceFilterNode | null | undefined): AudienceFilterLeaf[] {
  if (!node) return [];
  if (isFilterLeaf(node)) return [node];
  const children = "and" in node ? node.and : node.or;
  return children.flatMap(filterTreeLeaves);
}

/** Nesting depth: a leaf is 0, a group is one more than its deepest child. */
export function filterTreeDepth(node: AudienceFilterNode | null | undefined): number {
  if (!node || isFilterLeaf(node)) return 0;
  const children = "and" in node ? node.and : node.or;
  return 1 + Math.max(0, ...children.map(filterTreeDepth));
}

/**
 * Whether a tree says nothing v2 could not: a leaf, or an `and` of leaves.
 *
 * The rule that decides which contract version the editor writes. Everything
 * else — any `or`, any nesting — is v3.
 */
export function isFlatConjunction(node: AudienceFilterNode | null | undefined): boolean {
  if (!node || isFilterLeaf(node)) return true;
  if ("or" in node) return false;
  return node.and.every(isFilterLeaf);
}

/**
 * A v3 plan the serve path will actually honour, or `null` for "no lens".
 *
 * Mirrors both backend parsers: closed key sets, an unknown facet anywhere in
 * the tree VOIDS the plan (under `or` it might have admitted authors, under
 * `and` removed them), a threshold only on a countable facet, the caps on
 * leaves and depth, and the six-distinct-facet budget across tree and boost.
 */
export function normaliseAudiencePlanV3(plan: AudiencePlanV3): AudiencePlanV3 | null {
  if (!audienceOnEmptyModes.includes(plan.on_empty)) return null;

  const filter = plan.filter === null ? null : normaliseFilterNode(plan.filter);
  if (plan.filter !== null && filter === null) return null;
  if (filter && filterTreeLeaves(filter).length > MAX_AUDIENCE_FILTER_LEAVES) return null;
  if (filter && filterTreeDepth(filter) > MAX_AUDIENCE_FILTER_DEPTH) return null;

  // The boost half is v2's, unchanged.
  const asV2 = normaliseAudiencePlan({ v: AUDIENCE_CONTRACT_VERSION, filters: [], boost: plan.boost });
  const boost = asV2?.boost ?? null;
  if (plan.boost && !boost) return null;

  if (filter === null && boost === null) return null;

  const distinct = new Set<BlendableLensFacet>([
    ...filterTreeLeaves(filter).map((l) => l.facet),
    ...(boost?.blend.map((e) => e.facet) ?? []),
  ]);
  if (distinct.size > MAX_LENS_BLEND_ENTRIES) return null;

  return { v: AUDIENCE_CONTRACT_VERSION_V3, filter, on_empty: plan.on_empty, boost };
}

function normaliseFilterNode(node: AudienceFilterNode): AudienceFilterNode | null {
  if (isFilterLeaf(node)) {
    if (!blendableLensFacets.includes(node.facet)) return null;
    const leaf: AudienceFilterLeaf = { facet: node.facet };
    if (node.min !== undefined) {
      if (
        !isCountableFacet(node.facet) ||
        !Number.isInteger(node.min) ||
        node.min < 1 ||
        node.min > MAX_AUDIENCE_MIN_COUNT
      ) {
        return null;
      }
      leaf.min = node.min;
    }
    return leaf;
  }
  const children = "and" in node ? node.and : node.or;
  if (!Array.isArray(children) || children.length === 0) return null;
  const out: AudienceFilterNode[] = [];
  for (const child of children) {
    const normalised = normaliseFilterNode(child);
    if (normalised === null) return null;
    out.push(normalised);
  }
  return "and" in node ? { and: out } : { or: out };
}

/**
 * A filter tree as prose, with its own grammar: leaves joined by "and"/"or",
 * nested groups in parentheses because "A and B or C" reads two ways and the
 * tree reads one. Mirrors `_describe_filter_node` in feed-processor so the Gym
 * page and the editor say the same thing about the same lens.
 */
export function describeFilterTree(node: AudienceFilterNode, top = true): string {
  const named = (facet: string) =>
    blendableLensFacetOptions.find((o) => o.value === facet)?.label.toLowerCase() ?? facet;
  if (isFilterLeaf(node)) {
    const threshold =
      node.min === undefined ? "" : ` reached by at least ${node.min} of the people they follow`;
    return `${named(node.facet)}${threshold}`;
  }
  const word = "and" in node ? " and " : " or ";
  const children = "and" in node ? node.and : node.or;
  const parts = children.map((c) => describeFilterTree(c, false)).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!;
  const joined = parts.join(word);
  return top ? joined : `(${joined})`;
}

function describeAudiencePlanV3(plan: AudiencePlanV3): string {
  const parts: string[] = [];
  if (plan.filter) {
    const empty =
      plan.on_empty === "strict" ? "nothing is shown" : "the feed is shown unfiltered";
    parts.push(`Only posts from ${describeFilterTree(plan.filter)}; when nothing matches, ${empty}.`);
  }
  if (plan.boost) {
    const names = plan.boost.blend
      .map((e) => blendableLensFacetOptions.find((o) => o.value === e.facet)?.label ?? e.facet)
      .join(", ");
    parts.push(`Moves up posts from ${names.toLowerCase()}. ${describeBoostStrength(plan.boost.strength)}`);
  }
  return parts.join(" ");
}

export const audienceOnEmptyOptions: {
  value: AudienceOnEmpty;
  label: string;
  hint: string;
}[] = [
  {
    value: "strict",
    label: "Show nothing",
    hint: "The reader sees an empty feed. Honest, and what a private-audience feed means — but most readers share very few authors with any given feed, so expect this often.",
  },
  {
    value: "unfiltered",
    label: "Show the feed unfiltered",
    hint: "Falls back to the whole feed rather than an empty one. Safer, at the cost of the filter not always applying.",
  },
];

export function isAudiencePlan(lens: unknown): lens is AudiencePlan {
  if (typeof lens !== "object" || lens === null) return false;
  const candidate = lens as Partial<AudiencePlan>;
  return (
    candidate.v === AUDIENCE_CONTRACT_VERSION && Array.isArray(candidate.filters)
  );
}

/**
 * How far a boost can actually move a post, in plain words.
 *
 * ⚠️ This is the number the design note got wrong. It promised "at most
 * `S·(n−1)` places", which is false for the formula that shipped — solving
 * `(1−S)·base_X + S > (1−S)·base_i` gives `(n−1)·S/(1−S)`, and feeder-rs pins
 * it as tight in `a_boost_moves_a_post_by_the_documented_bound_and_no_further`.
 * At 30% on a 30-item page that is 12 places, not 9, and from 50% up the boost
 * can reorder the page freely.
 *
 * Saying so plainly is the whole point of this feature: a dial that promises
 * less than it does is the same class of mistake as a lens labelled "People I
 * follow" that does not filter.
 *
 * And the copy says "within each page" because that is the other thing creators
 * read wrong (2026-09-13, a "move up people they follow" boost was expected to
 * make the feed mostly follows): a boost reorders the ~30 posts already fetched
 * and cannot reach further into the pool. Only a filter changes what is fetched.
 */
export function describeBoostStrength(strength: number, pageSize = 30): string {
  if (strength <= 0) return "At 0% the boost is off: the feed's own order is untouched.";
  const pct = Math.round(strength * 100);
  if (strength >= 0.5) {
    return `At ${pct}% the audience outweighs the feed's own order: it decides where posts sit on each ${pageSize}-post page, never which posts are on it.`;
  }
  const places = Math.floor(((pageSize - 1) * strength) / (1 - strength));
  return `At ${pct}% a post from the audience can climb up to about ${places} places on a ${pageSize}-post page. Nothing is added or removed.`;
}

/**
 * A plan the serve path will actually honour, or `null` for "no lens".
 *
 * Mirrors both backend parsers rather than trusting them, so the editor never
 * saves something that silently serves unlensed. In particular it enforces the
 * two rules that are easy to lose:
 *
 * - a threshold on a boolean facet is dropped rather than saved, because all
 *   three parsers refuse the whole setting over one;
 * - the facet budget counts DISTINCT facets across both sections, since each
 *   one costs a single blob fetch however many layers name it.
 */
export function normaliseAudiencePlan(plan: AudiencePlan): AudiencePlan | null {
  const filters: AudienceFilter[] = [];
  for (const layer of plan.filters) {
    if (!blendableLensFacets.includes(layer.facet)) continue;
    if (!audienceOnEmptyModes.includes(layer.on_empty)) continue;
    const next: AudienceFilter = {
      facet: layer.facet,
      on_empty: layer.on_empty,
    };
    if (
      layer.min !== undefined &&
      isCountableFacet(layer.facet) &&
      Number.isInteger(layer.min) &&
      layer.min >= 1 &&
      layer.min <= MAX_AUDIENCE_MIN_COUNT
    ) {
      next.min = layer.min;
    }
    filters.push(next);
  }

  let boost: AudienceBoost | null = null;
  if (plan.boost) {
    const seen = new Map<BlendableLensFacet, number>();
    for (const entry of plan.boost.blend) {
      if (!blendableLensFacets.includes(entry.facet)) continue;
      if (!Number.isFinite(entry.weight) || entry.weight < 0) continue;
      const weight = Math.min(1, Math.max(0, entry.weight));
      const existing = seen.get(entry.facet);
      seen.set(
        entry.facet,
        existing === undefined ? weight : Math.max(existing, weight),
      );
    }
    const strength = plan.boost.strength;
    if (seen.size > 0 && Number.isFinite(strength)) {
      boost = {
        strength: Math.min(1, Math.max(0, strength)),
        combine: plan.boost.combine,
        blend: [...seen].map(([facet, weight]) => ({ facet, weight })),
      };
    }
  }

  if (filters.length === 0 && boost === null) return null;

  const distinct = new Set<BlendableLensFacet>([
    ...filters.map((f) => f.facet),
    ...(boost?.blend.map((e) => e.facet) ?? []),
  ]);
  if (distinct.size > MAX_LENS_BLEND_ENTRIES) return null;

  return { v: AUDIENCE_CONTRACT_VERSION, filters, boost };
}

/** Creator-facing one-liner for an audience plan. */
function describeAudiencePlan(plan: AudiencePlan): string {
  const parts: string[] = [];
  for (const layer of plan.filters) {
    const label =
      blendableLensFacetOptions.find((o) => o.value === layer.facet)?.label ??
      layer.facet;
    const threshold =
      layer.min === undefined
        ? ""
        : ` reached by at least ${layer.min} of the people they follow`;
    const empty =
      layer.on_empty === "strict"
        ? "nothing is shown"
        : "the feed is shown unfiltered";
    parts.push(
      `Only posts from ${label.toLowerCase()}${threshold}; when nothing matches, ${empty}.`,
    );
  }
  if (plan.boost) {
    const names = plan.boost.blend
      .map(
        (e) =>
          blendableLensFacetOptions.find((o) => o.value === e.facet)?.label ??
          e.facet,
      )
      .join(", ");
    parts.push(
      `Moves up posts from ${names.toLowerCase()}. ${describeBoostStrength(plan.boost.strength)}`,
    );
  }
  return parts.join(" ");
}

/**
 * Recipes: the same weights the old presets carried, one click away.
 *
 * An honest cost of moving from presets to explicit layers is that a preset
 * string was retunable server-side — changing what `network` weighed was a
 * deploy, not a migration — while a layer stack written here is frozen. These
 * are the mitigation, not a replacement for it: the creator still has to click
 * to pick up a retune.
 *
 * Every one is a BOOST, deliberately. Restoring any of these as a filter would
 * cause the outage this redesign exists to prevent: with `on_empty: "strict"`,
 * a follows filter hands ~94% of readers an empty page. The filter is offered
 * separately, as something a creator chooses with the consequence spelled out.
 */
export const audienceRecipes: {
  id: string;
  label: string;
  hint: string;
  plan: AudiencePlan;
}[] = [
  {
    id: "network",
    label: "Their network",
    hint: "People they follow first, then who those people follow.",
    plan: {
      v: AUDIENCE_CONTRACT_VERSION,
      filters: [],
      boost: {
        strength: 1,
        combine: "max",
        blend: [
          { facet: "follows", weight: 1 },
          { facet: "follows2", weight: 0.5 },
        ],
      },
    },
  },
  {
    id: "discover",
    label: "Discovery",
    hint: "Niche favourites and fresh finds first. Leaves out people they already follow.",
    plan: {
      v: AUDIENCE_CONTRACT_VERSION,
      filters: [],
      boost: {
        strength: 1,
        combine: "max",
        blend: [
          { facet: "niche", weight: 1 },
          { facet: "velocity", weight: 0.9 },
          { facet: "follows2", weight: 0.4 },
        ],
      },
    },
  },
  {
    id: "expertise",
    label: "This feed's authorities",
    hint: "Who this feed's own authors follow, lifted by the reader's network.",
    plan: {
      v: AUDIENCE_CONTRACT_VERSION,
      filters: [],
      boost: {
        strength: 1,
        combine: "sum",
        blend: [
          { facet: "domain", weight: 1 },
          { facet: "follows", weight: 0.5 },
          { facet: "follows2", weight: 0.25 },
        ],
      },
    },
  },
];

/**
 * Express a legacy setting as an audience plan, for the Convert button.
 *
 * A v1 `filter` policy becomes a BOOST, not a filter. That is not a
 * simplification: v1's filter could never empty a page — it fell back to
 * reordering below `min_items` — so converting it into a v2 filter would hand
 * the feed a power it never had, and with the new strict default that means
 * blank feeds for most readers. Turning a converted boost into a real filter is
 * a decision the creator makes afterwards, with the warning in front of them.
 */
export function lensToAudience(lens: LensSetting): AudiencePlan | AudiencePlanV3 | null {
  if (isAudiencePlan(lens)) return lens;
  if (isAudiencePlanV3(lens)) return lens;
  if (isLensBlend(lens)) {
    return normaliseAudiencePlan({
      v: AUDIENCE_CONTRACT_VERSION,
      filters: [],
      boost: {
        strength: 1,
        combine: lens.combine,
        blend: lens.blend,
      },
    });
  }
  const recipe = audienceRecipes.find((r) => r.id === lens);
  if (recipe) return recipe.plan;
  // A single-facet preset becomes a single-facet boost at full strength, which
  // is what the preset did.
  if ((blendableLensFacets as readonly string[]).includes(lens)) {
    return {
      v: AUDIENCE_CONTRACT_VERSION,
      filters: [],
      boost: {
        strength: 1,
        combine: "max",
        blend: [{ facet: lens as BlendableLensFacet, weight: 1 }],
      },
    };
  }
  return null;
}

/**
 * Orderings a lens may draw from.
 *
 * Mirrors `feed_mix.MIXABLE_SORT_ORDERS` in feed-processor. `foryou` needs a viewer did and
 * `personalized` never transits the feed service, so neither can be one lens of several — and a
 * list naming one is refused server-side, which would show up as a saved feed that serves
 * unlensed rather than as an error.
 */
export const lensOrderings = [
  "new",
  "old",
  "hackernews",
  "trending",
  "wilson",
  "blend",
] as const;
export type LensOrdering = (typeof lensOrderings)[number];

/**
 * What a creator calls each sort — THE ONE SET OF NAMES, used by every control that names a sort.
 *
 * 🔴 THIS PANEL USED TO INVENT ITS OWN. The feed's Sorting control says "Trending", "Best Of",
 * "Wilson Trending"; the split editor said "Rising now", "Busiest discussions", "Consistently
 * popular" — different names for the same six values, two fields apart in the same editor. A
 * creator who picked "Trending" could not find it in the list of things to split between.
 *
 * Prettier names are not worth a second vocabulary. If these need to change they change here, for
 * both controls at once, which is the point of the map.
 *
 * Declared in the same order as the Sorting control renders them, so the two dropdowns read alike.
 */
export const sortOrderLabels: Record<LensOrdering, string> = {
  new: "New",
  trending: "Trending",
  blend: "Blend",
  old: "Old",
  hackernews: "Best Of",
  wilson: "Wilson Trending",
};

export const lensOrderingOptions: { value: LensOrdering; label: string }[] = (
  ["new", "trending", "blend", "old", "hackernews", "wilson"] as const
).map((value) => ({ value, label: sortOrderLabels[value] }));

/**
 * How many sort layers one feed may divide its page between.
 *
 * Mirrors `feed_mix.MAX_LANES`, which is 10 as of feed-processor#184. Ten became affordable only
 * once the lanes were fetched CONCURRENTLY (#180) — each layer is still its own ClickHouse query,
 * so ten serial round trips against a 500ms serve budget was the thing standing in the way.
 *
 * 🔴 Ten is also close to the arithmetic ceiling, independently of query cost: a 20-post page has
 * twenty slots, and past ten layers they begin competing for single slots.
 */
const MAX_FEED_LENSES = 10;

/**
 * The smallest share a layer may hold: none.
 *
 * Devin, 2026-09-16: a creator dials a layer all the way to 0% to switch it off without losing
 * its ordering, parameters and audience. The serve path (`feed_lenses.parse_lenses`) accepts a
 * zero share and asks that lane for no slots; the Feed Gym's ARMS still may not starve a layer
 * under 5% (`gym_deltas._validate_lens_list`), but that is the gym's rule, not the editor's. Kept
 * as a named constant so the arithmetic in `rebalanceShares` reads as a floor rather than a magic
 * zero — and so a floor could come back in one place if it ever had to.
 */
export const MIN_LENS_SHARE = 0;

/** One lens of a feed that divides its page between several. */
export interface FeedLens {
  /**
   * Durable identity, minted by `newLayerId` and never reused.
   *
   * 🔴 Optional because a feed stored before ids existed has none, and `feed_lenses._layer_id`
   * synthesises `"{i}:{sort_order}"` for those — byte-identical to the positional cursor key
   * their readers are already holding. Writing an id onto a legacy layer here is therefore a
   * REAL change: it moves that layer's cursor. Mint ids for layers a creator adds, and leave
   * existing ones alone until they are edited anyway.
   */
  id?: string;
  share: number;
  sort_order: LensOrdering;
  lens?: LensSetting;
  /**
   * This layer's own ordering settings — decay, window, multipliers.
   *
   * Open-ended on purpose: `feed_lenses.LANE_KEYS` is only `{share, sort_order, id}`, so every
   * other key overrides the feed's own for this lane's query. The editor exposes the subset in
   * `layerSettingFields`; the wire carries whatever is set.
   *
   * ⚠️ An index signature is permissive — it matches the flat wire format honestly, but it also
   * means a mistyped setting name typechecks. `layerSettingFields` is the guard: read and write
   * settings through it rather than by literal key, and a typo shows up as a control that is
   * never rendered rather than as a value the serve path silently ignores.
   */
  [setting: string]: unknown;
}

/**
 * The lens list on a feed, or undefined when it has one lens or none.
 *
 * Total, like `readableLens`: anything this build cannot interpret reads as "not a list", which is
 * the same answer the serve path gives. A single entry is deliberately not a list — one lens is
 * what `custom_sort_settings.lens` already describes, and `parse_lenses` refuses a one-entry list.
 */
export function readableLensList(
  value: unknown,
  fallbackOrder?: LensOrdering,
): FeedLens[] | undefined {
  // One layer is LEGAL, mirroring `feed_lenses.parse_lenses` since feed-processor#190 — a feed
  // that has only ever had one ordering has to be able to hold one layer, or it can never grow a
  // second. Zero is still not a list.
  if (!Array.isArray(value) || value.length < 1 || value.length > MAX_FEED_LENSES) {
    return undefined;
  }
  const out: FeedLens[] = [];
  for (const entry of value) {
    if (typeof entry !== "object" || entry === null) return undefined;
    const record = entry as Record<string, unknown>;
    const share = record.share;
    // Zero is a legal share — a layer switched off (Devin, 2026-09-16) — and `normaliseLensList`
    // has accepted it since fp#270. This check still said `<= 0` until 2026-09-21, so a feed
    // SAVED with a 0% layer came back UNREADABLE: the editor fell back to the single-layer
    // panel and the next save would have overwritten the whole list. Only negative refuses.
    if (typeof share !== "number" || !Number.isFinite(share) || share < 0) return undefined;

    // A lane may omit its ordering and take the feed's, which is what `parse_lenses` does
    // (`order = entry.get("sort_order") or sort_order`). Refusing it here instead would be worse
    // than strict: the server would serve the list while the editor showed the single-lens panel,
    // and the first save would overwrite a configuration the creator could not see.
    const order = record.sort_order === undefined ? fallbackOrder : record.sort_order;
    if (typeof order !== "string" || !(lensOrderings as readonly string[]).includes(order)) {
      return undefined;
    }

    const lens = readableLens(record.lens);
    // 🔴 THE LAYER'S OWN KEYS ARE CARRIED THROUGH, not rebuilt from a fixed shape. Everything
    // that is not `share`/`sort_order`/`lens` is a per-layer ORDERING SETTING — decay, window,
    // multipliers — which `feed_lenses.LANE_KEYS` lets a lane override for its own query. Listing
    // only the fields this build knows about silently discarded the settings a creator configured
    // and the layer's durable `id` with them.
    out.push({
      ...layerOwnKeys(record),
      share,
      sort_order: order as LensOrdering,
      ...(lens ? { lens } : {}),
    });
  }
  return out;
}

/**
 * A layer's own ordering settings: everything that is not its share, its sort or its audience.
 *
 * 🔴 THE LIST IS DEFINED BY EXCLUSION, deliberately. `feed_lenses.LANE_KEYS` is only
 * `{share, sort_order, id}`, so every OTHER key in a stored lane overrides the feed's own for that
 * lane's query — decay, window, multipliers, and whatever is added next. Enumerating the keys this
 * build knows about instead is what silently dropped a creator's settings, and it would drop the
 * next setting added too.
 *
 * `id` is carried as part of this, because it must survive a round trip exactly as the settings do.
 */
function layerOwnKeys(lane: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(lane)) {
    if (key === "share" || key === "sort_order" || key === "lens") continue;
    out[key] = value;
  }
  return out;
}

/** Sort orders that cannot be a layer at all — served by a different system entirely. */
export function isPersonalizedOrder(order: string): boolean {
  // `personalized` is served by personalization_layer under its own service DID and never transits
  // feeder-rs; `foryou` is in one of this codebase's four disagreeing sort-order lists and raises
  // NameError at serve time. Neither can hold a layer, so neither is rendered as one.
  return order === "personalized" || order === "foryou";
}


/** Six decimal places, which is what `parse_lenses` rounds a share to. */
function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/**
 * The list the serve path will actually honour, or `null`.
 *
 * Mirrors `feed_lenses.parse_lenses` rather than trusting it, for the same reason
 * `normaliseLensBlend` mirrors its two parsers: the editor must never save something that silently
 * serves unlensed. Shares are normalised here because they are normalised there, so what the
 * creator sees after saving is what the page will be.
 */
export function normaliseLensList(lenses: FeedLens[]): FeedLens[] | null {
  if (lenses.length < 1 || lenses.length > MAX_FEED_LENSES) return null;
  const total = lenses.reduce((sum, lane) => sum + (lane.share > 0 ? lane.share : 0), 0);
  if (total <= 0) return null;
  const out: FeedLens[] = [];
  for (const lane of lenses) {
    // Zero is a legal share — a layer switched off — so only a negative or non-numeric one is
    // refused, matching `parse_lenses`.
    if (!(lane.share >= 0)) return null;
    if (!(lensOrderings as readonly string[]).includes(lane.sort_order)) return null;
    // Six places, matching the backend's rounding, so a round trip does not drift the numbers a
    // creator is looking at.
    const share = round6(lane.share / total);
    if (lenses.length > 1 && share < MIN_LENS_SHARE) return null;
    const lens = lane.lens && isLensBlend(lane.lens) ? normaliseLensBlend(lane.lens) : lane.lens;
    // Same reason as `readableLensList`: rebuilding a lane from a fixed shape drops its own
    // ordering settings and its `id`. Normalising a list must change the SHARES and nothing else.
    out.push({
      ...layerOwnKeys(lane as unknown as Record<string, unknown>),
      share,
      sort_order: lane.sort_order,
      ...(lens ? { lens } : {}),
    });
  }
  return out;
}

/**
 * Fit shares into a budget without letting any fall below the floor.
 *
 * Proportional scaling alone is not enough when there is a floor: scaling 0.99 and 0.01 into a
 * budget of 0.95 leaves the second at 0.0095, under a 5% floor, and clamping it up on its own
 * would overrun the budget. So this water-fills — allocate proportionally, pin anything that came out under the floor AT the
 * floor, and re-share what remains among the rest. At most one pass per lane, so it terminates.
 *
 * Always succeeds when `budget >= floor * shares.length`, which the caller guarantees.
 */
function fitShares(shares: number[], budget: number, floor: number): number[] {
  const out = shares.map((s) => (s > 0 ? s : 0));
  const pinned = out.map(() => false);

  for (let pass = 0; pass <= out.length; pass++) {
    const free = out.map((_, i) => i).filter((i) => !pinned[i]);
    if (free.length === 0) break;

    const spent = out.reduce((sum, v, i) => sum + (pinned[i] ? v : 0), 0);
    const left = budget - spent;
    const freeTotal = free.reduce((sum, i) => sum + (out[i] ?? 0), 0);

    let violated = false;
    for (const i of free) {
      // An equal split when nothing has any weight — there is no ratio to preserve, and leaving
      // them at zero would make the list unservable.
      const weight = freeTotal > 0 ? (out[i] ?? 0) / freeTotal : 1 / free.length;
      const value = left * weight;
      if (value < floor) {
        out[i] = floor;
        pinned[i] = true;
        violated = true;
      } else {
        out[i] = value;
      }
    }
    if (!violated) break;
  }
  return out;
}

/**
 * Hold one lens at a share and refit the others around it.
 *
 * Shares are weights on the wire — `parse_lenses` divides each by the total — so nothing stops a
 * saved list summing to 5. But a PERCENTAGE control cannot display a weight: `CanvasPercentageInput`
 * clamps at 100%, so a list of `3` and `2` renders as "100%" and "100%" and a creator cannot see
 * what they set. Rather than swap in a raw-number control and ask people to think in weights, the
 * panel keeps the list summing to 1, which makes the percentage the truth.
 *
 * The edited lens gets exactly what was typed, clamped only where the arithmetic leaves no choice:
 * every other lens needs `MIN_LENS_SHARE`, so one lens cannot exceed `1 - floor * (n - 1)`. The
 * rest are refitted in proportion to what they had, so adjusting one audience does not silently
 * reorder the others' relative sizes.
 *
 * The result is always servable — which is why the panel's refusal message is reachable only from a
 * list that arrived unservable, never from an edit made here.
 */
export function rebalanceShares(
  lenses: FeedLens[],
  index: number,
  share: number,
): FeedLens[] {
  const others = lenses.length - 1;
  // With one layer there is nothing to rebalance AGAINST: it owns the whole page whatever is
  // typed, so the share control is read-only in that state rather than pretending to accept a
  // value the serve path would renormalise straight back to 1.0.
  if (lenses.length < 2 || index < 0 || index >= lenses.length) return lenses;

  const ceiling = 1 - MIN_LENS_SHARE * others;
  // More lenses than the page can give a floor to. Unreachable while the floor is 0, but the
  // guard is cheap and the alternative is inventing negative shares.
  if (ceiling < MIN_LENS_SHARE) return lenses;

  const held = Math.min(Math.max(Number.isFinite(share) ? share : 0, MIN_LENS_SHARE), ceiling);
  const rest = fitShares(
    lenses.filter((_, i) => i !== index).map((lane) => lane.share),
    1 - held,
    MIN_LENS_SHARE,
  );

  let cursor = 0;
  return lenses.map((lane, i) => ({
    ...lane,
    // Six places, matching `normaliseLensList` and the backend, so a round trip does not drift the
    // numbers a creator is looking at.
    share: i === index ? round6(held) : round6(rest[cursor++] ?? MIN_LENS_SHARE),
  }));
}

/**
 * A layer's durable id, minted here and never reused.
 *
 * 🔴 RANDOM, NOT `max(existing) + 1`. A derived counter is derived state, and two editor tabs
 * saving one feed would derive the SAME next id — the component-version-id collision this codebase
 * has already recorded once. Mirrors `feed_lenses.new_layer_id`, six base36 characters, kept short
 * because the id is the per-lane cursor key and ten of them are the bulk of an encoded cursor.
 */
export function newLayerId(): string {
  const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz";
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

/**
 * The layer this one duplicates, or null.
 *
 * Mirrors `feed_lenses._refuse_indistinguishable`: identity is the whole configuration —
 * ordering, settings AND audience — because that is what makes a second copy meaningful. The
 * SHARE is deliberately excluded: the same layer at 30% and 20% is still one layer asked for
 * twice, just unevenly.
 */
/**
 * Why the serve path would refuse this list, in the creator's words — or `null` when it would
 * serve it.
 *
 * Mirrors `feed_lenses.parse_lenses` on feed-processor: more layers than `MAX_FEED_LENSES`, a
 * negative share, shares that add up to nothing, or two layers that differ in nothing but share
 * and id (`_refuse_indistinguishable`). 🔴 A refused list does not error at serve time — the feed
 * silently falls back to its single ordering, unlensed, and nothing tells the creator. So Save
 * asks this first and refuses in the editor, where the creator can still see the layer it names.
 */
export function lensListRefusal(value: unknown): string | null {
  // `custom_sort_settings` is free-form JSONB; anything that is not a list of lanes is not this
  // function's business (the parser refuses those with its own words).
  if (!Array.isArray(value) || value.length === 0) return null;
  if (!value.every((l) => l && typeof l === "object" && typeof (l as FeedLens).share === "number")) {
    return null;
  }
  const lenses = value as FeedLens[];
  if (lenses.length > MAX_FEED_LENSES) {
    return `A page can have at most ${MAX_FEED_LENSES} layers; this one has ${lenses.length}.`;
  }
  if (lenses.some((l) => !(l.share >= 0))) {
    return "A layer's share of the page cannot be negative.";
  }
  if (lenses.reduce((sum, l) => sum + l.share, 0) <= 0) {
    return "Every layer is at 0%. Give at least one layer a share of the page.";
  }
  for (let i = 0; i < lenses.length; i++) {
    const twin = duplicateOf(lenses, i);
    if (twin !== null) {
      return (
        `Layer ${i + 1} is the same as layer ${twin + 1}: same ordering, same settings, same ` +
        `audience. Change one of them or remove it — the feed would serve without any layers ` +
        `otherwise.`
      );
    }
  }
  return null;
}

export function duplicateOf(lenses: FeedLens[], index: number): number | null {
  const fingerprint = (lane: FeedLens) =>
    JSON.stringify([
      lane.sort_order,
      Object.fromEntries(
        Object.entries(lane as unknown as Record<string, unknown>)
          .filter(([k]) => k !== "share" && k !== "id" && k !== "sort_order")
          .sort(([a], [b]) => a.localeCompare(b)),
      ),
    ]);
  const mine = fingerprint(lenses[index]!);
  for (let i = 0; i < index; i++) {
    if (fingerprint(lenses[i]!) === mine) return i;
  }
  return null;
}

/**
 * The composite presets, which are recipes over the blendable facets rather than facets of their
 * own — so they are the three preset names with no third-person label to borrow. Kept in step with
 * `PRESET_LABELS` in `feed_lenses.py`, which the Gym page's own prose comes through.
 */
const presetOnlyLabels: Record<string, string> = {
  network: "their network",
  discover: "accounts they have not found yet",
  expertise: "the experts in this feed's world",
};

/** "a", "a and b", "a, b and c" — an Oxford-less list, because these read as prose. */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * One lens as a short phrase, carrying its own preposition.
 *
 * `describeLensSetting` is deliberately long: it is the copy beneath a single audience, where a
 * full sentence about how far a boost can move a post is exactly what a creator needs. Splicing
 * those sentences into a comma-separated list of up to four does not work — it produces
 * "...not the feed's own ranking., 40% newest via ...", full stop and comma together.
 *
 * A list summary needs each audience NAMED. The panel above each one still does the explaining, so
 * nothing is lost by being terse here.
 *
 * Returns undefined for a lens that names no audience, so the caller writes "50% newest" rather
 * than "50% newest for ".
 */
export function describeLensBriefly(lens: LensSetting): string | undefined {
  const named = (facet: string) =>
    blendableLensFacetOptions.find((o) => o.value === facet)?.label.toLowerCase() ?? facet;

  if (isAudiencePlan(lens)) {
    // A FILTER-ONLY PLAN IS NAMED BY WHAT SURVIVES IT, and that branch is the whole reason this
    // handles the two halves separately. The boost is what names an audience, so summarising off
    // the blend alone renders `{filters: [...], boost: null}` — servable, and what the Feed Gym's
    // generated designs sample most — as an unlensed lane. `feed_lenses.describe` in
    // feed-processor did exactly that and rendered a two-lane page as "90% new; 10% new".
    // "limited to" rather than "for", because a filter removes rather than moves.
    if (lens.boost && lens.boost.blend.length > 0) {
      return `for ${joinNames(lens.boost.blend.map((e) => named(e.facet)))}`;
    }
    if (lens.filters.length > 0) {
      const audience = `limited to ${joinNames(lens.filters.map((f) => named(f.facet)))}`;
      // `strict` earns words even in the terse form, where a `min` does not. Both are in the panel
      // above, but only one of them is a WARNING: a strict filter can hand a reader a short page,
      // which is the fact a creator has to weigh before adopting this, and the row this appears on
      // is the collapsed state where the panel is not on screen. A threshold's number changes how
      // tight the audience is, not whether the page fills.
      return lens.filters.some((f) => f.on_empty === "strict")
        ? `${audience}, even when that leaves the page thin`
        : audience;
    }
    // A plan that neither boosts nor filters. Servable, and does nothing to the page.
    return undefined;
  }

  if (isAudiencePlanV3(lens)) {
    if (lens.boost && lens.boost.blend.length > 0) {
      return `for ${joinNames(lens.boost.blend.map((e) => named(e.facet)))}`;
    }
    if (lens.filter) {
      const audience = `limited to ${describeFilterTree(lens.filter)}`;
      return lens.on_empty === "strict"
        ? `${audience}, even when that leaves the page thin`
        : audience;
    }
    return undefined;
  }

  if (isLensBlend(lens)) {
    return `for ${joinNames(lens.blend.map((e) => named(e.facet)))}`;
  }

  // A bare preset from the older contract, in the READER's person. `lensFacetOptions` labels are
  // written as instructions to the creator — "Prioritize my network" — so stripping the verb left
  // "for my network" in a sentence about somebody else, and "for people i follow" with a lowercase
  // "i" in it. Every preset but the three composites is also a blendable facet, and those labels
  // are already third-person, so they answer for all but three. Mirrors `_facet_label` in
  // `feed_lenses`, which resolves the same two sets the same way round.
  const composite = presetOnlyLabels[lens];
  if (composite) return `for ${composite}`;
  if (!lensFacetOptions.some((o) => o.value === lens)) return undefined;
  return `for ${named(lens)}`;
}

/** Reader-facing one-liner: "60% rising now for people they follow, 40% newest". */
export function describeLensList(lenses: FeedLens[]): string {
  return lenses
    .map((lane) => {
      const order =
        lensOrderingOptions.find((o) => o.value === lane.sort_order)?.label ?? lane.sort_order;
      const audience = lane.lens ? describeLensBriefly(lane.lens) : undefined;
      return `${Math.round(lane.share * 100)}% ${order.toLowerCase()}${audience ? ` ${audience}` : ""}`;
    })
    .join(", ");
}
